/**
 * BrainViz — bucketed two-tier "brain" progress visual (zero dependencies).
 *
 * STATE MODEL: each of the 12 regions is divided into N buckets (N = section
 * count for the topic mapped to that region). Every bucket carries independent
 * { t1, t2 } state, so sections complete out of order and a single section can
 * go t1→t2 before its siblings start. Sequential is enforced ONLY within a
 * bucket (t2 needs t1).
 *
 *   import BrainViz from '@/components/brain/BrainViz.js';
 *   import { loadBrainGeometry } from '@/components/brain/brainGeometryLoader.js';
 *   const geometry = await loadBrainGeometry();
 *   const brain = new BrainViz('#brain', { geometry, sectionCounts:{g6:27,...} });
 *   await brain.advanceSection('g6', 3, { tier:1 });  // section reaches L2 → light nodes
 *   await brain.advanceSection('g6', 3, { tier:2 });  // same section reaches L3 → bolster lines
 *   brain.flash('g6');                                // L1 ack (no persisted change)
 *   brain.setProgress(state);                         // { g6:[{t1,t2},...] } — instant
 *
 * Geometry is INJECTED (not imported) — fetched once via brainGeometryLoader.
 * Styles live in css/components/brain.css (cascade layer), not injected here.
 */

const NS = "http://www.w3.org/2000/svg";
const _hx = (h, o) => parseInt(h.slice(o, o + 2), 16);
const LUM = (h) => (0.299 * _hx(h, 1) + 0.587 * _hx(h, 3) + 0.114 * _hx(h, 5)) / 255;
const darker = (a, b) => (LUM(a) <= LUM(b) ? a : b);

export class BrainViz {
  constructor(container, opts = {}) {
    this.el = typeof container === "string" ? document.querySelector(container) : container;
    if (!this.el) throw new Error("BrainViz: container not found");
    if (!opts.geometry || !opts.geometry.nodes) throw new Error("BrainViz: opts.geometry is required");

    this.opts = Object.assign({
      geometry: null,
      hlMs: 1000, fadeMs: 500, popMs: 600, buildMs: 1200,
      deepen: "line",            // 'off' | 'line' | 'dark'
      reducedMotion: "auto",
      background: "transparent",
      flashOpacity: 0.8,
      flashUniform: true,        // flash the whole region in one dark color (darkest node)
      ungrayLeadMs: 250,         // start the dimmed-node recolor this many ms BEFORE settle
      overshoot: false,          // ratio-adaptive rewind/overshoot flourish
      sectionCounts: null,       // { g1:n, ... } — required for real bucketing
      colors: null,              // { g1:'#hex', ... } — per-region color override
      onAdvance: null,           // ({region,bucket,tier,progress}) at each settle
    }, opts);

    const geo = this.opts.geometry;
    this.geometry = geo;
    this.nodes = geo.nodes;
    this.edges = geo.edges;
    this.clusters = geo.clusters;
    this.regions = Object.keys(geo.cats);

    // Palette (with optional override)
    this.cats = {};
    for (const r of this.regions) {
      this.cats[r] = Object.assign({}, geo.cats[r]);
      if (this.opts.colors && this.opts.colors[r]) this.cats[r].color = this.opts.colors[r];
    }

    // Section counts per region (clamped to available node count)
    this.sectionCounts = {};
    for (const r of this.regions) {
      const want = (this.opts.sectionCounts && this.opts.sectionCounts[r]) || 1;
      this.sectionCounts[r] = Math.max(1, Math.min(this._regionNodeCount(r), want));
    }

    this._queue = []; this._running = false; this._timers = []; this._overshootEls = [];
    this.last = null; this.phase = null; this._ungray = false;

    this._reduced = this.opts.reducedMotion === "auto"
      ? matchMedia("(prefers-reduced-motion: reduce)").matches : !!this.opts.reducedMotion;

    this._precomputeGeometry();
    this._build();
    this._rebucket();
    this._resetState();
    this._paint();
  }

  // ---- public API ----------------------------------------------------------

  /** Advance one bucket (= one section) of a region. Auto-picks tier (t1 then t2)
   *  unless forced. t2 requires t1 on the SAME bucket. Returns a Promise. */
  advanceSection(region, bucket, { tier } = {}) {
    this._assertRegion(region);
    const st = this.buckets[region][bucket];
    if (!st) throw new Error(`BrainViz: ${region} has no bucket ${bucket}`);
    const t = tier || (!st.t1 ? 1 : !st.t2 ? 2 : 0);
    if (!t || (t === 2 && !st.t1) || (t === 1 && st.t1) || (t === 2 && st.t2)) {
      return Promise.resolve(this.getProgress());
    }
    return this._enqueue({ region, bucket, tier: t });
  }

  /** Lowest bucket with work left for the given tier (or -1). */
  nextBucket(region, tier = 1) {
    this._assertRegion(region);
    const b = this.buckets[region];
    for (let i = 0; i < b.length; i++) {
      if (tier === 1 && !b[i].t1) return i;
      if (tier === 2 && b[i].t1 && !b[i].t2) return i;
    }
    return -1;
  }

  /** Re-divide regions into a new number of buckets (taxonomy changed). Resets state. */
  setSectionCounts(map = {}) {
    for (const r in map) {
      if (this.sectionCounts[r] == null) continue;
      this.sectionCounts[r] = Math.max(1, Math.min(this._regionNodeCount(r), map[r] | 0));
    }
    this._clearTimers(); this._queue = []; this._running = false;
    this._rebucket(); this._resetState(); this._paint();
  }

  /** Load saved per-bucket state instantly (no animation). */
  setProgress(state = {}) {
    this._clearTimers(); this._queue = []; this._running = false; this.phase = null; this.last = null;
    for (const r of this.regions) {
      const incoming = state[r];
      if (!incoming) continue;
      incoming.forEach((x, b) => {
        if (!this.buckets[r][b]) return;
        this.buckets[r][b].t1 = !!x.t1;
        this.buckets[r][b].t2 = !!(x.t2 && x.t1); // enforce within-section ordering
      });
    }
    this._paint();
  }

  getProgress() {
    const o = {};
    for (const r of this.regions) o[r] = this.buckets[r].map((x) => ({ t1: x.t1, t2: x.t2 }));
    return o;
  }

  /** 0..1 overall progress for a region/topic (drives the synced progress bar).
   *  L2 (t1) is worth 0.66 of a section, L3 (t2) the remaining 0.34. */
  topicProgress(region) {
    const b = this.buckets[region];
    if (!b.length) return 0;
    let s = 0;
    for (const x of b) { if (x.t1) s += 0.66; if (x.t2) s += 0.34; }
    return s / b.length;
  }

  flash(region) {
    this._assertRegion(region);
    const nb = this.nextBucket(region, 1);
    return this._enqueue({ region, bucket: nb === -1 ? 0 : nb, flash: true });
  }
  fillAll() { for (const r of this.regions) this.buckets[r].forEach((x) => { x.t1 = true; x.t2 = true; }); this.phase = null; this.last = null; this._paint(); }
  reset() { this._clearTimers(); this._queue = []; this._running = false; this._resetState(); this._paint(); }
  setReducedMotion(v) { this._reduced = !!v; this._syncRoot(); this._paint(); }
  info(region) { return { name: this.cats[region].name, color: this.cats[region].color, sections: this.sectionCounts[region], buckets: this.getProgress()[region] }; }
  destroy() { this._clearTimers(); this.el.innerHTML = ""; this.el.classList.remove("brainviz", "reduced"); }

  // ---- internals ------------------------------------------------------------
  _assertRegion(r) { if (!this.cats[r]) throw new Error("BrainViz: unknown region " + r); }
  _regionNodeCount(r) { return this.nodes.reduce((s, n) => s + (n.cluster === r ? 1 : 0), 0); }
  _syncRoot() { this.el.classList.toggle("reduced", this._reduced); }

  _precomputeGeometry() {
    this._len = this.edges.map((e) => Math.hypot(this.nodes[e.a].x - this.nodes[e.b].x, this.nodes[e.a].y - this.nodes[e.b].y));
    this._dir = this.edges.map((e) => (this.nodes[e.a].depth <= this.nodes[e.b].depth ? 1 : -1));
    const cx = this.nodes.reduce((s, n) => s + n.x, 0) / this.nodes.length;
    const cy = this.nodes.reduce((s, n) => s + n.y, 0) / this.nodes.length;
    // Darkest node color per region (by luminance) — used by flash/deepen.
    this._darkest = (() => {
      const best = {};
      this.nodes.forEach((n) => { const L = LUM(n.color); if (best[n.cluster] === undefined || L < best[n.cluster].L) best[n.cluster] = { c: n.color, L }; });
      const o = {}; for (const k in best) o[k] = best[k].c; return o;
    })();
    // Line color = the region's canonical color for the build animation.
    // Bridges (no cluster) take their outer endpoint's region color.
    this._lc = this.edges.map((e) => {
      const cluster = e.cluster || this.nodes[
        Math.hypot(this.nodes[e.a].x - cx, this.nodes[e.a].y - cy) >=
        Math.hypot(this.nodes[e.b].x - cx, this.nodes[e.b].y - cy) ? e.a : e.b].cluster;
      return this.cats[cluster].color;
    });
  }

  /** Assign every node and intra-cluster edge to a bucket (0..N-1) for its region.
   *  Nodes are ordered by depth (outward from hub) then split into contiguous
   *  groups; the remainder lands in the earliest buckets. */
  _rebucket() {
    this.nodeBucket = new Array(this.nodes.length).fill(-1);
    this.edgeBucket = new Array(this.edges.length).fill(-1);
    this.regionNodes = {};

    for (const r of this.regions) {
      const idx = [];
      this.nodes.forEach((n, i) => { if (n.cluster === r) idx.push(i); });
      const hub = this.clusters[r].hub;
      const hx = this.nodes[hub].x, hy = this.nodes[hub].y;
      idx.sort((a, b) =>
        (this.nodes[a].depth - this.nodes[b].depth) ||
        (Math.hypot(this.nodes[a].x - hx, this.nodes[a].y - hy) - Math.hypot(this.nodes[b].x - hx, this.nodes[b].y - hy)));
      this.regionNodes[r] = idx;

      const N = this.sectionCounts[r];
      const base = Math.floor(idx.length / N), extra = idx.length % N;
      let p = 0;
      for (let b = 0; b < N; b++) {
        const size = base + (b < extra ? 1 : 0);
        for (let k = 0; k < size; k++) this.nodeBucket[idx[p++]] = b;
      }
    }

    // Intra-cluster edges inherit the OUTER endpoint's bucket; bridges stay unbucketed.
    this.edges.forEach((e, i) => {
      if (!e.cluster) return;
      const cands = [];
      if (this.nodes[e.a].cluster === e.cluster) cands.push(this.nodeBucket[e.a]);
      if (this.nodes[e.b].cluster === e.cluster) cands.push(this.nodeBucket[e.b]);
      this.edgeBucket[i] = cands.length ? Math.max(...cands) : -1;
    });
  }

  _resetState() {
    this.buckets = {};
    for (const r of this.regions) this.buckets[r] = Array.from({ length: this.sectionCounts[r] }, () => ({ t1: false, t2: false }));
    this.last = null; this.phase = null; this._ungray = false; this._clearOvershoot();
  }

  _build() {
    this.el.classList.add("brainviz");
    this.el.style.setProperty("--hl", this.opts.hlMs + "ms");
    this.el.style.setProperty("--fade", this.opts.fadeMs + "ms");
    this.el.style.setProperty("--pop", this.opts.popMs + "ms");
    this.el.style.setProperty("--flashOp", this.opts.flashOpacity);
    this.el.style.setProperty("--bv-bg", this.opts.background);
    this._syncRoot();
    const svg = document.createElementNS(NS, "svg");
    // Tight viewBox: frame to the actual drawn content (+ a small margin) so the
    // visible brain fills its container — the declared world box has ~50% padding.
    let minx = 1e9, miny = 1e9, maxx = -1e9, maxy = -1e9;
    for (const n of this.nodes) {
      minx = Math.min(minx, n.x - n.r); maxx = Math.max(maxx, n.x + n.r);
      miny = Math.min(miny, n.y - n.r); maxy = Math.max(maxy, n.y + n.r);
    }
    const pad = 14;
    const viewBox = { x: minx - pad, y: miny - pad, w: (maxx - minx) + 2 * pad, h: (maxy - miny) + 2 * pad };
    svg.setAttribute("viewBox", `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`);
    svg.setAttribute("aria-hidden", "true");
    const gl = document.createElementNS(NS, "g"), gn = document.createElementNS(NS, "g");
    this.lineEls = this.edges.map((e) => {
      const l = document.createElementNS(NS, "line");
      l.setAttribute("x1", this.nodes[e.a].x); l.setAttribute("y1", this.nodes[e.a].y);
      l.setAttribute("x2", this.nodes[e.b].x); l.setAttribute("y2", this.nodes[e.b].y);
      l.setAttribute("class", "line"); gl.appendChild(l); return l;
    });
    this.nodeGs = []; this.cores = [];
    this.nodes.forEach((n) => {
      const g = document.createElementNS(NS, "g"); g.setAttribute("class", "node tier-future" + (n.hub ? " hub" : ""));
      const c = document.createElementNS(NS, "circle"); c.setAttribute("class", "core"); c.setAttribute("cx", n.x); c.setAttribute("cy", n.y); c.setAttribute("r", n.r);
      g.appendChild(c); gn.appendChild(g); this.nodeGs.push(g); this.cores.push(c);
    });
    svg.appendChild(gl); svg.appendChild(gn);
    this.el.innerHTML = ""; this.el.appendChild(svg); this.svg = svg;
  }

  _reached(i) { const b = this.nodeBucket[i]; return b >= 0 && this.buckets[this.nodes[i].cluster][b] && this.buckets[this.nodes[i].cluster][b].t1; }
  _nbol(i) { const b = this.nodeBucket[i]; return b >= 0 && this.buckets[this.nodes[i].cluster][b] && this.buckets[this.nodes[i].cluster][b].t2; }
  _inActiveBucket(i) { return this.last && this.nodes[i].cluster === this.last.region && this.nodeBucket[i] === this.last.bucket; }
  _deepColor(i) { const m = this.opts.deepen, n = this.nodes[i]; if (m === "off") return n.color; if (m === "dark") return this._darkest[n.cluster]; return darker(n.color, this.cats[n.cluster].color); }
  _colFor(i) { return this._nbol(i) ? this._deepColor(i) : this.nodes[i].color; }

  /** Ordered node indices in the currently-active bucket (for pop stagger). */
  _activeRank() {
    if (!this.last) return { rank: {}, count: 0 };
    const items = this.regionNodes[this.last.region].filter((i) => this.nodeBucket[i] === this.last.bucket);
    const rank = {}; items.forEach((idx, r) => (rank[idx] = r));
    return { rank, count: items.length };
  }

  _paint() {
    const ai = this._activeRank();
    const buildMs = this.opts.buildMs;
    const step = ai.count > 1 ? Math.min(180, (buildMs - this.opts.popMs) / (ai.count - 1)) : 0;

    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i], r = this._reached(i);
      let base = r ? "tier-earned" : "tier-future";
      const t1b = this.phase === "build" && this.last && this.last.tier === 1 && this._inActiveBucket(i) && r;
      if (t1b) base = "tier-active";
      let cls = "node " + base + (n.hub ? " hub" : "");
      // Highlight/flash lights the ENTIRE active region (not just the active
      // bucket) — bucket-scoping is only for the build phase below.
      const isFlashing = (this.phase === "highlight" || this.phase === "flash") && this.last && n.cluster === this.last.region;
      if (isFlashing) cls += " flash";
      if (this.phase != null && this.last && !this._ungray && r && n.cluster !== this.last.region) cls += " gray";
      if (this.phase === "build" && this.last && this.last.tier === 2 && this._inActiveBucket(i) && r) cls += " alive";
      const g = this.nodeGs[i];
      g.setAttribute("class", cls);
      g.style.setProperty("--col", this._colFor(i)); // node's real base color (start/end of flash)
      // Flash PEAK color, separate from --col so earned nodes start/end at their
      // real color and only flash TO this (uniform dark, or their own color).
      g.style.setProperty("--flash-col", (isFlashing && this.opts.flashUniform) ? this._darkest[n.cluster] : this._colFor(i));
      g.style.setProperty("--d", (t1b && !this._reduced ? (ai.rank[i] || 0) * step : 0) + "ms");
    }

    for (let i = 0; i < this.edges.length; i++) {
      const e = this.edges[i], a = e.a, b = e.b;
      const bothR = this._reached(a) && this._reached(b);
      const eb = this.edgeBucket[i], ec = e.cluster;
      const isBol = eb >= 0 && this.buckets[ec] && this.buckets[ec][eb] && this.buckets[ec][eb].t2 && bothR;
      const inActiveRegion = this.last && ec === this.last.region;
      const touchesActiveBucket = inActiveRegion && eb === this.last.bucket;
      const boosting = this.phase === "build" && this.last && this.last.tier === 2 && touchesActiveBucket && isBol;

      let state;
      if (this.phase != null && this.last && !this._ungray && !inActiveRegion && bothR) state = "grayed";
      else if (boosting) state = "bolstering";
      else if (isBol) state = "bolstered";
      else if (bothR) state = "baseline";
      else state = "faint";

      const cls = "line" + (state === "bolstered" ? " bolstered" : state === "bolstering" ? " bolstering" : state === "grayed" ? " grayed" : state === "faint" ? " faint" : "");
      const el = this.lineEls[i];
      el.setAttribute("class", cls);
      el.style.setProperty("--lc", this._lc[i]);
      el.style.setProperty("--len", this._len[i]);
      el.style.setProperty("--dashoff", this._dir[i] * this._len[i]);
      if (state === "bolstering") {
        const mr = Math.max(ai.rank[a] || 0, ai.rank[b] || 0);
        el.style.setProperty("--d", (this._reduced ? 0 : mr * step) + "ms");
      }
    }
  }

  // ---- animation queue ------------------------------------------------------
  _enqueue(job) { return new Promise((res) => { this._queue.push({ job, res }); this._pump(); }); }
  _pump() {
    if (this._running) return;
    const next = this._queue.shift(); if (!next) return;
    this._running = true;
    const done = () => { this._running = false; next.res(this.getProgress()); this._pump(); };
    if (next.job.flash) this._runFlash(next.job.region, next.job.bucket, done);
    else this._runAdvance(next.job, done);
  }

  _runAdvance(job, done) {
    const { region, bucket, tier } = job;
    const st = this.buckets[region][bucket];
    if (!st || (tier === 2 && !st.t1) || (tier === 1 && st.t1) || (tier === 2 && st.t2)) { done(); return; }

    this.last = { region, bucket, tier };
    this.phase = "highlight"; this._clearTimers(); this._paint();
    const hl = this._reduced ? 0 : this.opts.hlMs;
    const bd = this._reduced ? 0 : this.opts.buildMs;

    this._timers.push(setTimeout(() => {
      if (tier === 1) st.t1 = true; else st.t2 = true;
      this.phase = "build"; this._ungray = false; this._paint();
      this._overshoot(region, bucket, tier);
      // Early un-gray: begin recoloring the dimmed (non-active) nodes/lines this
      // many ms BEFORE settle, so the recolor overlaps the build animation's tail.
      const lead = this._reduced ? 0 : Math.min(bd, this.opts.ungrayLeadMs || 0);
      if (lead > 0) {
        this._timers.push(setTimeout(() => { this._ungray = true; this._paint(); }, bd - lead));
      }
      this._timers.push(setTimeout(() => {
        this.phase = null; this.last = null; this._ungray = false; this._clearOvershoot(); this._paint();
        if (this.opts.onAdvance) this.opts.onAdvance({ region, bucket, tier, progress: this.topicProgress(region) });
        done();
      }, bd));
    }, hl));
  }

  _runFlash(region, bucket, done) {
    this.last = { region, bucket: Math.max(0, bucket), tier: 1 };
    this.phase = "flash"; this._clearTimers(); this._paint();
    this._timers.push(setTimeout(() => { this.phase = null; this.last = null; this._paint(); done(); }, this._reduced ? 0 : this.opts.hlMs));
  }

  /** Transient rewind/overshoot flourish. Ratio-adaptive: where a region has
   *  comfortable headroom (>=1.5 nodes/section) we briefly light a node of the
   *  NEXT bucket (true overshoot), then drop it on settle. Where it's tight
   *  (~1 node/section) we instead over-pop the bucket's own nodes so the beat
   *  reads without erasing the increment. Never touches real state. */
  _overshoot(region, bucket, tier) {
    if (!this.opts.overshoot || this._reduced || tier !== 1) return;
    const ratio = this.regionNodes[region].length / this.sectionCounts[region];
    if (ratio >= 1.5) {
      const next = this.regionNodes[region].find((i) => this.nodeBucket[i] === bucket + 1);
      if (next != null) {
        const g = this.nodeGs[next];
        g.style.setProperty("--col", this._colFor(next));
        g.classList.add("overshoot");
        this._overshootEls = [g];
      }
    } else {
      const own = this.regionNodes[region].filter((i) => this.nodeBucket[i] === bucket);
      this._overshootEls = own.map((i) => this.nodeGs[i]);
      own.forEach((i) => this.nodeGs[i].classList.add("flourish"));
    }
  }
  _clearOvershoot() { (this._overshootEls || []).forEach((g) => g.classList.remove("overshoot", "flourish")); this._overshootEls = []; }

  _clearTimers() { this._timers.forEach(clearTimeout); this._timers = []; }
}

export default BrainViz;
